import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

export interface UseOverflowItemsOptions<T extends string, TItem extends { id: T }> {
  items: readonly TItem[];
  /** Width kept free for the overflow trigger once any item overflows. */
  reservedWidth: number;
  /** Width kept free even when every item fits, e.g. for an always-present accessory. */
  baseReservedWidth?: number;
  /** Promoted into the visible set when it would otherwise overflow. */
  activeItemId?: T;
  /** Horizontal gap the row places between adjacent items. */
  itemGap?: number;
}

export const getVisibleOverflowItemIds = <T extends string>(
  items: readonly { id: T }[],
  itemWidths: ReadonlyMap<T, number>,
  activeItemId: T | undefined,
  availableWidth: number,
  reservedWidth: number,
  baseReservedWidth: number,
  itemGap: number,
): T[] => {
  if (availableWidth <= 0 || items.some((item) => !itemWidths.has(item.id))) {
    return items.map((item) => item.id);
  }

  // Charging every item a trailing gap and widening the budget by one gap prices n items at
  // n widths plus n - 1 gaps, matching the row's layout.
  const itemCost = (id: T) => (itemWidths.get(id) ?? 0) + itemGap;
  const totalWidth = items.reduce((total, item) => total + itemCost(item.id), 0);
  if (totalWidth <= Math.max(0, availableWidth + itemGap - baseReservedWidth)) {
    return items.map((item) => item.id);
  }

  const itemBudget = Math.max(0, availableWidth + itemGap - reservedWidth);
  const visibleIds: T[] = [];
  let visibleWidth = 0;

  for (const item of items) {
    const cost = itemCost(item.id);
    if (visibleWidth + cost > itemBudget) break;
    visibleIds.push(item.id);
    visibleWidth += cost;
  }

  if (activeItemId == null || visibleIds.includes(activeItemId)) {
    return visibleIds;
  }

  const activeWidth = itemCost(activeItemId);
  while (visibleIds.length > 0 && visibleWidth + activeWidth > itemBudget) {
    const removedId = visibleIds.pop();
    visibleWidth -= removedId == null ? 0 : itemCost(removedId);
  }
  visibleIds.push(activeItemId);
  return visibleIds;
};

/**
 * Splits a single-row list into the leading items that fit `containerRef` and the trailing items
 * that do not. Widths come from `measurementRefs`, which callers attach to an invisible copy of
 * every item so hidden items still have a measurable size.
 */
export const useOverflowItems = <T extends string, TItem extends { id: T }>({
  items,
  reservedWidth,
  baseReservedWidth = 0,
  activeItemId,
  itemGap = 0,
}: UseOverflowItemsOptions<T, TItem>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measurementRefs = useRef(new Map<T, HTMLElement>());
  const [visibleIds, setVisibleIds] = useState<readonly T[]>(() => items.map((item) => item.id));

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (container == null) return;

    const itemWidths = new Map<T, number>();
    for (const item of items) {
      const node = measurementRefs.current.get(item.id);
      if (node != null) itemWidths.set(item.id, node.offsetWidth);
    }

    const nextVisibleIds = getVisibleOverflowItemIds(
      items,
      itemWidths,
      activeItemId,
      container.clientWidth,
      reservedWidth,
      baseReservedWidth,
      itemGap,
    );
    setVisibleIds((current) =>
      current.length === nextVisibleIds.length &&
      current.every((id, index) => id === nextVisibleIds[index])
        ? current
        : nextVisibleIds,
    );
  }, [activeItemId, baseReservedWidth, itemGap, items, reservedWidth]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(measure);
    if (containerRef.current != null) observer.observe(containerRef.current);
    measurementRefs.current.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [measure]);

  const visibleIdSet = useMemo(() => new Set(visibleIds), [visibleIds]);
  const visibleItems = useMemo(
    () => items.filter((item) => visibleIdSet.has(item.id)),
    [items, visibleIdSet],
  );
  const overflowItems = useMemo(
    () => items.filter((item) => !visibleIdSet.has(item.id)),
    [items, visibleIdSet],
  );

  return { containerRef, measurementRefs, overflowItems, visibleItems };
};
